import styled from '@emotion/styled-base';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { userService } from 'src/config/rootService';
import { Button } from 'src/shared/components/Button';
import { Input, InputType } from 'src/shared/ds/components/Input';
import { genderOptions } from '../models/genderOptions';
import { statesOptions } from '../models/statesOptions';

export interface InsuranceMemberData {
    firstName?: string;
    lastName?: string;
    gender?: string;
    birthDate?: string;
    addressLine1?: string;
    addressLine2?: string;
    addressCity?: string;
    addressState?: string;
    addressZip?: string;
}

export interface InsurancePlanData {
    subscriberId: string;
    policyNumber: string;
}

export enum EnterInsuranceInfoFormTestID {
    Wrapper = 'EnterInsuranceInfoFormTestID.Wrapper',
    SubmitButton = 'EnterInsuranceInfoFormTestID.SubmitButton',
}

export interface Props {
    onCancel: string | (() => void);
    onFormSubmit: (formData: InsuranceMemberData & InsurancePlanData) => void;
}

const SUBSCRIBER_ID_MIN_LENGTH = 2;
const SUBSCRIBER_ID_MAX_LENGTH = 80;
const POLICY_NUMBER_MAX_LENGTH = 50;

const FormWrapper = styled('form')({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

const FormRow = styled('div')({
    display: 'flex',
    gap: 16,
    alignItems: 'flex-end',
});

const ButtonsRow = styled('div')({
    display: 'flex',
    paddingTop: 24,
    gap: 18,
});

export const EnterInsuranceInfoForm = (props: Props) => {
    const [subscriberId, setSubscriberId] = useState('');
    const [subscriberIdError, setSubscriberIdError] = useState('');
    const [policyNumber, setPolicyNumber] = useState('');
    const [policyNumberError, setPolicyNumberError] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [gender, setGender] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [addressCity, setAddressCity] = useState('');
    const [addressState, setAddressState] = useState('');
    const [addressZip, setAddressZip] = useState('');
    const [submitBlocked, setSubmitBlocked] = useState(true);
    const { t } = useTranslation();

    const errorFields = [subscriberIdError, policyNumberError];
    const requiredFields = [
        firstName,
        lastName,
        gender,
        birthDate,
        addressLine1,
        addressCity,
        addressState,
        addressZip,
        subscriberId,
        policyNumber,
    ];

    const handleSubscriberIdChange = value => {
        if (value.length > SUBSCRIBER_ID_MAX_LENGTH || value.length < SUBSCRIBER_ID_MIN_LENGTH) {
            setSubscriberIdError(t('insuranceForm:insurance_form.subscriber_id_error'));
        } else {
            setSubscriberIdError('');
        }

        setSubscriberId(value);
    };

    const handlePolicyNumberChange = value => {
        if (value.length > POLICY_NUMBER_MAX_LENGTH) {
            setPolicyNumberError(t('insuranceForm:insurance_form.policy_number_error'));
        } else {
            setPolicyNumberError('');
        }

        setPolicyNumber(value);
    };

    const handleFirstNameChange = value => setFirstName(value);
    const handleLastNameChange = value => setLastName(value);
    const handleGenderChange = value => setGender(value);
    const handleBirthDateChange = value => setBirthDate(value);
    const handleAddressLine1Change = value => setAddressLine1(value);
    const handleAddressLine2Change = value => setAddressLine2(value);
    const handleAddressCityChange = value => setAddressCity(value);
    const handleAddressStateChange = value => setAddressState(value);
    const handleAddressZipChange = value => setAddressZip(value);

    const handleFormSubmit = (event?) => {
        if (event) {
            event.preventDefault();
        }

        if (submitBlocked) {
            return;
        }

        props.onFormSubmit({
            firstName,
            lastName,
            gender,
            birthDate,
            addressLine1,
            addressLine2,
            addressCity,
            addressState,
            addressZip,
            subscriberId,
            policyNumber,
        });
    };

    useEffect(() => {
        const hasErrors = !!errorFields.find(errorStr => !!errorStr);
        const hasMissingRequiredFields = requiredFields.includes('');

        setSubmitBlocked(hasErrors || hasMissingRequiredFields);
    }, [...errorFields, requiredFields]);

    useEffect(() => {
        userService.fetchUserInfo().then(value => {
            const {
                userDetails: {
                    firstName: retrievedFirstName,
                    lastName: retrievedLastName,
                    birthDate: retrievedBirthDate,
                    mailingAddress,
                    state,
                    zipCode,
                } = {},
            } = value;

            setFirstName(retrievedFirstName || '');
            setLastName(retrievedLastName || '');
            setBirthDate(retrievedBirthDate || '');
            setAddressLine1(mailingAddress || '');
            setAddressState(state || '');
            setAddressZip(zipCode || '');
        });
    }, []);

    return (
        <FormWrapper onSubmit={handleFormSubmit} data-testid={EnterInsuranceInfoFormTestID.Wrapper}>
            <FormRow>
                <Input
                    label={t('insuranceForm:insurance_form.insurance_subscriber_member_id')}
                    type={InputType.TextField}
                    fieldId="subscriberId"
                    value={subscriberId}
                    onChange={handleSubscriberIdChange}
                    error={subscriberIdError}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('insuranceForm:insurance_form.group_number')}
                    type={InputType.TextField}
                    fieldId="policyNumber"
                    value={policyNumber}
                    onChange={handlePolicyNumberChange}
                    error={policyNumberError}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('auth:registration_form.labels.firstName')}
                    type={InputType.TextField}
                    fieldId="firstName"
                    value={firstName}
                    onChange={handleFirstNameChange}
                />
                <Input
                    label={t('auth:registration_form.labels.lastName')}
                    type={InputType.TextField}
                    fieldId="lastName"
                    value={lastName}
                    onChange={handleLastNameChange}
                />
            </FormRow>

            <FormRow>
                <Input
                    fieldId="gender"
                    label={t('goalCategories:Gender')}
                    type={InputType.SingleSelect}
                    options={genderOptions.map(({ value, text }) => ({ value, text: t(text) }))}
                    value={gender}
                    onChange={handleGenderChange}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('auth:registration_form.labels.dateOfBirth')}
                    type={InputType.DatePicker}
                    value={birthDate}
                    fieldId="birthDate"
                    onChange={handleBirthDateChange}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('insuranceForm:insurance_form.address1')}
                    type={InputType.TextField}
                    value={addressLine1}
                    fieldId="addressLine1"
                    onChange={handleAddressLine1Change}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('insuranceForm:insurance_form.address2')}
                    type={InputType.TextField}
                    value={addressLine2}
                    fieldId="addressLine2"
                    onChange={handleAddressLine2Change}
                />
            </FormRow>

            <FormRow>
                <Input
                    label={t('providers:additional_info.city')}
                    type={InputType.TextField}
                    value={addressCity}
                    fieldId="addressCity"
                    onChange={handleAddressCityChange}
                />
            </FormRow>

            <FormRow>
                <Input
                    fieldId="addressState"
                    label={t('providers:additional_info.state')}
                    type={InputType.SingleSelect}
                    options={statesOptions.map(text => ({ value: text, text }))}
                    value={addressState}
                    onChange={handleAddressStateChange}
                />
                <Input
                    label={t('providers:additional_info.postal_code')}
                    type={InputType.TextField}
                    value={addressZip}
                    fieldId="addressZip"
                    onChange={handleAddressZipChange}
                />
            </FormRow>

            <ButtonsRow>
                {typeof props.onCancel === 'function' && (
                    <Button fullWidth type="white" onClick={props.onCancel}>
                        {t('translation:common.cancel')}
                    </Button>
                )}
                {typeof props.onCancel === 'string' && (
                    <Button fullWidth type="white" to={props.onCancel}>
                        {t('translation:common.cancel')}
                    </Button>
                )}
                <Button
                    fullWidth
                    testID={EnterInsuranceInfoFormTestID.SubmitButton}
                    disabled={submitBlocked}
                    onClick={handleFormSubmit}
                >
                    {t('translation:common.submit')}
                </Button>
            </ButtonsRow>
        </FormWrapper>
    );
};
